import { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  Database,
  Download,
  HelpCircle,
  Home,
  LayoutDashboard,
  Map,
  Plane,
  Settings,
} from 'lucide-react';
import { filterAnalysisByDate } from './domain/aggregators';
import type { AnalysisResult, DateRange } from './domain/models';
import { createNoiseSample, createTokyoDaySample, createTokyoOsakaTripSample, createUnknownSample } from './samples/generateSampleData';
import { deleteAllAnalysis, loadLatestAnalysis, saveAnalysis } from './storage/repositories';
import { parseTimelineFile, parseTimelineText, type ImportProgress } from './workers/parserClient';
import { DashboardPage } from './routes/DashboardPage';
import { DayDetailPage } from './routes/DayDetailPage';
import { ExportPage } from './routes/ExportPage';
import { HelpPage } from './routes/HelpPage';
import { HomePage } from './routes/HomePage';
import { MapPage } from './routes/MapPage';
import { SettingsPage } from './routes/SettingsPage';
import { TripsPage } from './routes/TripsPage';

type View = 'home' | 'dashboard' | 'map' | 'day' | 'trips' | 'export' | 'settings' | 'help';

const navItems: Array<{ id: View; label: string; icon: typeof Home; requiresResult?: boolean }> = [
  { id: 'home', label: 'Upload', icon: Home },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, requiresResult: true },
  { id: 'map', label: 'Map', icon: Map, requiresResult: true },
  { id: 'day', label: 'Day Detail', icon: CalendarDays, requiresResult: true },
  { id: 'trips', label: 'Trips', icon: Plane, requiresResult: true },
  { id: 'export', label: 'Export', icon: Download, requiresResult: true },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'help', label: 'Help', icon: HelpCircle },
];

export default function App() {
  const [view, setView] = useState<View>('home');
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [progress, setProgress] = useState<ImportProgress | undefined>();
  const [importError, setImportError] = useState<string | null>(null);
  const [storageMessage, setStorageMessage] = useState<string | null>(null);

  useEffect(() => {
    loadLatestAnalysis()
      .then((stored) => {
        if (stored) {
          setAnalysis(stored);
          setStorageMessage('ローカル保存済みの最新解析結果を読み込みました');
          setView('dashboard');
        }
      })
      .catch(() => {
        setStorageMessage('ローカル保存データを読み込めませんでした');
      });
  }, []);

  const visibleAnalysis = useMemo(
    () => (analysis ? filterAnalysisByDate(analysis, dateRange) : null),
    [analysis, dateRange],
  );

  const importResult = async (parse: () => Promise<AnalysisResult>) => {
    setImportError(null);
    setProgress({ stage: '準備中', progress: 0 });
    try {
      const result = await parse();
      setAnalysis(result);
      setDateRange({});
      setProgress(undefined);
      setStorageMessage(null);
      setView(result.points.length > 0 ? 'dashboard' : 'home');
    } catch (error) {
      setProgress(undefined);
      setImportError(error instanceof Error ? error.message : '解析中にエラーが発生しました');
      setView('home');
    }
  };

  const handleFile = (file: File) => {
    void importResult(() => parseTimelineFile(file, setProgress));
  };

  const handleSample = (kind: 'day' | 'trip' | 'noise' | 'unknown') => {
    const sample = {
      day: createTokyoDaySample,
      trip: createTokyoOsakaTripSample,
      noise: createNoiseSample,
      unknown: createUnknownSample,
    }[kind]();
    const text = JSON.stringify(sample);
    void importResult(() => parseTimelineText(text, `sample-${kind}.json`, new Blob([text]).size, setProgress));
  };

  const handleSave = async () => {
    if (!analysis) return;
    await saveAnalysis(analysis);
    setStorageMessage('解析結果をこのブラウザに保存しました');
  };

  const handleDeleteAll = async () => {
    await deleteAllAnalysis();
    setStorageMessage('ローカル保存データを削除しました');
  };

  const handleAnalysisChange = (result: AnalysisResult) => {
    setAnalysis(result);
  };

  const disabled = Boolean(progress);
  const activeView = viewRequiresResult(view) && !analysis ? 'home' : view;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">TL</span>
          <div className="brand-title">
            <strong>Timeline Local Studio</strong>
            <span>Client-side viewer</span>
          </div>
        </div>

        <nav className="nav-list" aria-label="Primary">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isDisabled = item.requiresResult && !analysis;
            return (
              <button
                key={item.id}
                className={`nav-button${activeView === item.id ? ' active' : ''}`}
                type="button"
                disabled={isDisabled}
                onClick={() => setView(item.id)}
              >
                <Icon size={17} aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <Database size={16} aria-hidden="true" />
          <div>No upload / No telemetry / IndexedDB opt-in</div>
        </div>
      </aside>

      <main className="main">
        {renderView({
          view: activeView,
          analysis,
          visibleAnalysis,
          dateRange,
          disabled,
          progress,
          importError,
          storageMessage,
          setDateRange,
          onFile: handleFile,
          onSample: handleSample,
          onSave: handleSave,
          onDeleteAll: handleDeleteAll,
          onAnalysisChange: handleAnalysisChange,
        })}
      </main>
    </div>
  );
}

function viewRequiresResult(view: View): boolean {
  return navItems.some((item) => item.id === view && item.requiresResult);
}

function renderView(props: {
  view: View;
  analysis: AnalysisResult | null;
  visibleAnalysis: AnalysisResult | null;
  dateRange: DateRange;
  disabled: boolean;
  progress?: ImportProgress;
  importError: string | null;
  storageMessage: string | null;
  setDateRange: (range: DateRange) => void;
  onFile: (file: File) => void;
  onSample: (kind: 'day' | 'trip' | 'noise' | 'unknown') => void;
  onSave: () => void;
  onDeleteAll: () => void;
  onAnalysisChange: (result: AnalysisResult) => void;
}) {
  switch (props.view) {
    case 'dashboard':
      return props.analysis ? <DashboardPage result={props.analysis} onAnalysisChange={props.onAnalysisChange} /> : null;
    case 'map':
      return props.visibleAnalysis && props.analysis ? (
        <MapPage result={props.visibleAnalysis} fullResult={props.analysis} range={props.dateRange} onRangeChange={props.setDateRange} />
      ) : null;
    case 'day':
      return props.visibleAnalysis ? <DayDetailPage result={props.visibleAnalysis} /> : null;
    case 'trips':
      return props.visibleAnalysis ? <TripsPage result={props.visibleAnalysis} /> : null;
    case 'export':
      return props.visibleAnalysis ? <ExportPage result={props.visibleAnalysis} /> : null;
    case 'settings':
      return <SettingsPage result={props.analysis} storageMessage={props.storageMessage} onSave={props.onSave} onDeleteAll={props.onDeleteAll} />;
    case 'help':
      return <HelpPage />;
    case 'home':
    default:
      return (
        <HomePage
          disabled={props.disabled}
          progress={props.progress}
          result={props.analysis}
          error={props.importError}
          warnings={props.analysis?.warnings}
          onFile={props.onFile}
          onSample={props.onSample}
        />
      );
  }
}
